import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { guidFor } from '@ember/object/internals'
import { service } from '@ember/service'
import { action, get } from '@ember/object'
import { groupBy } from 'lodash'
import type { IntlService } from 'ember-intl'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { t } from 'ember-intl'
import { GradeSelect } from 'teacher-ui-client/components/base/select/grade-position'
import { AllGradesOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { on } from '@ember/modifier'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { concat } from '@ember/helper'
import { eq, or } from 'ember-truth-helpers'
import { fullGradeName } from 'teacher-ui-client/helpers/full-grade-name'
import { array } from '@ember/helper'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import { trackedFunction } from 'reactiveweb/function'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { assert } from '@ember/debug'

interface DrivingTestAssignmentOption extends AssignmentOption {
  category: string
}

interface Signature {
  Args: {
    /**
     * Should only use with RE Driving Tests or MS Driving Tests
     */
    precinct: Precinct
    selectAssignmentAction: (option?: AssignmentOption) => void
    selectedAssignmentOption?: AssignmentOption
  }
}

export class ActivitySelectorDrivingTestsList extends Component<Signature> {
  @service intl!: IntlService

  @service subscriptionType!: SubscriptionTypeService

  @service lessonInfo!: LessonInfoService

  loadOptions = trackedFunction(this, async () => {
    assert(
      'unexpected precinct for driving-tests-list',
      this.args.precinct === Precinct.RE_DRIVING_TESTS || this.args.precinct === Precinct.MS_DRIVING_TESTS,
    )

    await this.lessonInfo.loadLessonInfoFor([this.args.precinct])
    return this.lessonInfo.lessonsFor(this.args.precinct).map<DrivingTestAssignmentOption>((data) => {
      let label = ''
      if (this.args.precinct === Precinct.RE_DRIVING_TESTS) {
        const formattedCategory = this.intl.t(`driving_tests.re.categories.${data.category}`)
        label = `${data.lesson_number} - ${data.name} (${formattedCategory})`
      } else if (this.args.precinct === Precinct.MS_DRIVING_TESTS) {
        const formattedCategory = this.intl.t(`driving_tests.maths.categories.${data.category}`)
        label = `${data.grade_lesson_number} - ${data.name} (${formattedCategory})`
      }
      return {
        id: String(data.id),
        label,
        gradePosition: data.grade_position,
        assignmentData: {
          assignment_id: String(data.lesson_number), // note: not an id, resets at grade lesson number, only 'unique' with category
          assignment_type: 'lesson',
          grade_position: data.grade_position,
          grade_lesson_number: data.grade_lesson_number,
          category: data.category,
        },
        category: data.category ?? '',
      }
    })
  })

  get options() {
    return this.loadOptions.value ?? []
  }

  get componentId() {
    return guidFor(this)
  }

  @tracked currentGradePosition: number = AllGradesOption.id

  @tracked currentCategory = ''

  get gradePositions() {
    const gradePositions = this.options.map((option) => option.gradePosition)

    const definedGradePositions = gradePositions.filter((pos): pos is number => typeof pos === 'number')
    const uniqGradePositions = [...new Set(definedGradePositions)]
    return uniqGradePositions.sort((a, b) => a - b)
  }

  get categories() {
    const categories = this.options.map((option) => option.category)

    const definedCategories = categories.filter((cat): cat is string => typeof cat === 'string')
    const uniqCategories = [...new Set(definedCategories)]
    return uniqCategories.sort()
  }

  get optionsFilteredByGradePosition() {
    return this.options.filter(
      (option) =>
        this.currentGradePosition === AllGradesOption.id || option.gradePosition === this.currentGradePosition,
    )
  }

  /**
   * A category option is disabled if there are no lessons of that category for the selected grade
   */
  get categoryOptions() {
    const { optionsFilteredByGradePosition } = this
    return this.categories.map((category) => ({
      category,
      isDisabled: !optionsFilteredByGradePosition.some((lesson) => lesson.category === category),
    }))
  }

  get optionsGroupedByGradePosition() {
    // Also filter the lessons to group by the current category
    const filteredByCategory = this.optionsFilteredByGradePosition.filter(
      (lesson) => this.currentCategory === '' || lesson.category === this.currentCategory,
    )
    return groupBy(filteredByCategory, 'gradePosition')
  }

  get gradePositionGroupKeys() {
    return Object.keys(this.optionsGroupedByGradePosition).map((key) => Number.parseInt(key, 10))
  }

  @action
  selectGradePosition(gradePosition: number) {
    this.currentGradePosition = gradePosition
  }

  @action
  selectCategory(category: string) {
    this.currentCategory = category
  }

  @action
  selectOption(optionId: string) {
    const foundOption = this.options.find((option) => option.id === optionId)
    this.args.selectAssignmentAction(foundOption)
  }

  <template>
    <div class="flex justify-between md:flex-wrap">
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-grade-label" class="mb-1">
          {{t "grade.label"}}
        </label>
        <GradeSelect
          class="w-full"
          name="activity-grade"
          aria-labelledby="{{this.componentId}}-assignment-grade-label"
          @additionalOptions={{array AllGradesOption}}
          @gradePositions={{this.gradePositions}}
          @gradePosition={{this.currentGradePosition}}
          @updateGradePosition={{this.selectGradePosition}}
          @themeKey={{themeKey this.subscriptionType.current}}
        />
      </div>
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-categories-label" class="mb-1">
          {{t "category.label"}}
        </label>
        <BaseSelect
          data-test-select-activity-category
          name="activity-category"
          class="w-full"
          value={{this.currentCategory}}
          aria-labelledby="{{this.componentId}}-assignment-categories-label"
          @themeKey={{themeKey this.subscriptionType.current}}
          {{on "input" (eventValue this.selectCategory)}}
        >
          <option value="">{{t "category.all"}}</option>
          {{#each this.categoryOptions as |opt|}}
            <option
              value={{opt.category}}
              selected={{eq opt.category this.currentCategory}}
              disabled={{opt.isDisabled}}
            >
              {{#if (eq @precinct Precinct.RE_DRIVING_TESTS)}}
                {{t (concat "driving_tests.re.categories." opt.category)}}
              {{else if (eq @precinct Precinct.MS_DRIVING_TESTS)}}
                {{t (concat "driving_tests.maths.categories." opt.category)}}
              {{/if}}
            </option>
          {{/each}}
        </BaseSelect>
      </div>
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-lessons-label" class="mb-1">
          {{t "assignments.create.activityLesson"}}
        </label>
        <BaseSelect
          data-test-select-lesson
          class="w-full"
          name="activity-lesson"
          value={{or @selectedAssignmentOption.id ""}}
          aria-labelledby="{{this.componentId}}-assignment-lessons-label"
          @themeKey={{themeKey this.subscriptionType.current}}
          {{on "input" (eventValue this.selectOption)}}
        >
          <option value="">{{t "assignments.create.activityLessonPlaceholder"}}</option>
          {{#each this.gradePositionGroupKeys as |gradePosition|}}
            <optgroup label={{fullGradeName gradePosition}}>
              {{#each (get this.optionsGroupedByGradePosition gradePosition) as |optionItem|}}
                <option value={{optionItem.id}} selected={{eq optionItem.id @selectedAssignmentOption.id}}>
                  {{optionItem.label}}
                </option>
              {{/each}}
            </optgroup>
          {{/each}}
        </BaseSelect>
      </div>
    </div>
  </template>
}
